/**
 * Client-side reading of a user's access grants (F21).
 *
 * The server is still the authority: it re-checks every console-URL / connect
 * call. These helpers only decide which button a page shows, from data the
 * frontend already fetches: GET /resources (to find a project's resource), its
 * /resources/:id/access-status, and the user's own approved requests
 * (/access-requests/my-approvals).
 */
import type { AccessRequest } from '../types';

/** An approved request whose window (approved_at + duration) has not run out. */
export function isActiveApproval(req: Pick<AccessRequest, 'status' | 'approved_at' | 'duration_seconds'>, now = Date.now()): boolean {
    if (req.status !== 'approved') return false;
    if (req.approved_at && req.duration_seconds) {
        const approvedAt = new Date(req.approved_at).getTime();
        if (Number.isNaN(approvedAt)) return false;
        return now < approvedAt + req.duration_seconds * 1000;
    }
    return true;
}

export interface GcpProjectRef {
    /** Internal row id from /gcp/projects. */
    id?: string;
    /** The GCP project id, e.g. my-project-123. */
    project_id: string;
    project_name?: string;
}

interface ResourceLike {
    id: string;
    type?: string;
    external_id?: string;
    metadata?: Record<string, unknown> | null;
}

/** A /resources row that represents (or lives in) this GCP project. */
export function resourceMatchesGcpProject(r: ResourceLike, p: GcpProjectRef): boolean {
    if (!String(r.type ?? '').startsWith('gcp')) return false;
    const m = r.metadata ?? {};
    return (
        r.external_id === `gcp-${p.project_id}` ||
        r.external_id === p.project_id ||
        m.gcp_project_id === p.project_id ||
        m.project_id === p.project_id ||
        (!!p.id && m.gcp_project_internal_id === p.id)
    );
}

/** One of the user's requests that is for this project (by resource or metadata). */
export function requestMatchesGcpProject(
    req: Pick<AccessRequest, 'resource_id' | 'resource_type' | 'resource_name' | 'metadata'>,
    p: GcpProjectRef,
    projectResourceIds: ReadonlySet<string>,
): boolean {
    if (req.resource_id && projectResourceIds.has(req.resource_id)) return true;
    if (!String(req.resource_type ?? '').startsWith('gcp')) return false;
    return (
        req.metadata?.gcp_project_id === p.project_id ||
        req.resource_name === p.project_id ||
        (!!p.project_name && req.resource_name === p.project_name)
    );
}
